import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import "./CameraVideo.css";

interface CameraVideoProps {
  className?: string;
  onError?: (error: string) => void;
  onSuccess?: () => void;
  onPhotoCapture?: (photoBlob: Blob) => void;
}

export interface CameraVideoRef {
  capturePhoto: () => void;
}

const CameraVideo = forwardRef<CameraVideoRef, CameraVideoProps>(
  ({ className = "", onError, onSuccess, onPhotoCapture }, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [hasPermission, setHasPermission] = useState(false);

    useEffect(() => {
      startCamera();

      return () => {
        stopCamera();
      };
    }, []);

    const startCamera = async () => {
      try {
        setIsLoading(true);
        setError("");

        // Request camera permission and get video stream
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: "user", // Use front camera on mobile devices
          },
          audio: false,
        });

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          streamRef.current = stream;
          setHasPermission(true);
          onSuccess?.();
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to access camera";
        setError(errorMessage);
        onError?.(errorMessage);

        // Handle specific permission errors
        if (err instanceof DOMException) {
          if (err.name === "NotAllowedError") {
            setError("Camera permission denied. Please allow camera access.");
          } else if (err.name === "NotFoundError") {
            setError("No camera found on this device.");
          } else if (err.name === "NotReadableError") {
            setError("Camera is already in use by another application.");
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    const stopCamera = () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      setHasPermission(false);
    };

    const handleVideoLoad = () => {
      setIsLoading(false);
    };

    const handleVideoError = () => {
      setError("Failed to load video stream");
      setIsLoading(false);
    };

    const retryCamera = () => {
      stopCamera();
      startCamera();
    };

    // Capture photo from video stream
    const capturePhoto = () => {
      if (!videoRef.current || !hasPermission) {
        onError?.("Camera not available for photo capture");
        return;
      }

      const video = videoRef.current;
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      if (!context) {
        onError?.("Failed to create canvas context for photo capture");
        return;
      }

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw current video frame to canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (blob && onPhotoCapture) {
            onPhotoCapture(blob);
          } else {
            onError?.("Failed to capture photo");
          }
        },
        "image/jpeg",
        0.8 // Quality setting
      );
    };

    // Expose capturePhoto function via ref
    useImperativeHandle(ref, () => ({
      capturePhoto,
    }));

    if (error) {
      return (
        <div className={`camera-error ${className}`}>
          <div className="error-content">
            <div className="error-icon">📹</div>
            <p className="error-message">{error}</p>
            <button className="retry-button" onClick={retryCamera}>
              Retry Camera
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className={`camera-video-container ${className}`}>
        {isLoading && (
          <div className="camera-loading">
            <div className="loading-spinner"></div>
            <p>Accessing camera...</p>
          </div>
        )}

        <video
          ref={videoRef}
          className="camera-video"
          autoPlay
          playsInline
          muted
          onLoadedData={handleVideoLoad}
          onError={handleVideoError}
        />

        {hasPermission && !isLoading && (
          <div className="camera-status">
            <span className="status-indicator">●</span>
            <span className="status-text">Live</span>
          </div>
        )}
      </div>
    );
  }
);

CameraVideo.displayName = "CameraVideo";

export default CameraVideo;
